import React from 'react';
import { find, toString } from 'lodash';
import { reverse, shuffle } from 'lodash/fp';
import { Check, X } from 'react-feather';
import request from 'utils/request_ajax';
import { Button } from 'ui';
import { Text, TEXT_VARIANTS } from 'ui/Typography';
import RadioFieldGroup from 'components/shared/RadioFieldGroup';
import RadioField from 'components/shared/RadioFieldGroup/RadioField';
import QuestionElementPrompt from 'components/Questions/shared/QuestionElementPrompt';

const KnowledgeCheckFeedback = ({ correct, feedback }) => (
  <div className="KnowledgeCheckFeedback" aria-live="assertive">
    {correct && (
      <Text
        className="d-f ai-c"
        variant={TEXT_VARIANTS.INSTRUX_SEMIBOLD_SUCCESS}
      >
        <Check className="mr-4" />
        {I18n.t('questions.question.knowledge_check.feedback.correct')}
      </Text>
    )}
    {!correct && (
      <Text className="d-f ai-c" variant={TEXT_VARIANTS.INSTRUX_ERROR}>
        <X className="mr-4" />
        {I18n.t('questions.question.knowledge_check.feedback.incorrect')}
      </Text>
    )}
    <div>{feedback}</div>
  </div>
);

const MODES = {
  READY: { name: 'ready', feedback: false },
  FEEDBACK_CORRECT: { name: 'feedback_correct', feedback: true },
  FEEDBACK_INCORRECT: { name: 'feedback_incorrect', feedback: true },
};

class KnowledgeCheckShow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentChoice:
        find(this.optionsFromChoices(), { text: props.data.givenAnswer }) ||
        null,
      mode: MODES.READY,
      options: shuffle(this.optionsFromChoices()),
    };
  }

  componentDidMount() {
    this.goToNextMode();
  }

  optionsFromChoices() {
    return this.props.data.choices.map(choice => ({
      value: toString(choice.id),
      text: choice.text,
      correct: choice.correct,
      feedback: choice.feedback,
    }));
  }

  isInMode(mode) {
    return this.state.mode === mode;
  }

  scrambledOptions() {
    const { options } = this.state;
    if (options.length > 2) {
      return shuffle(this.state.options);
    }
    return reverse(this.state.options);
  }

  enterMode(mode) {
    let newState = { mode };
    if (mode === MODES.READY) {
      newState = {
        ...newState,
        currentChoice: null,
        options: this.scrambledOptions(),
      };
    }

    this.setState(newState);
  }

  isInFeedbackMode() {
    return this.state.mode.feedback;
  }

  saveAnswer() {
    const answerData = {
      answer: this.state.currentChoice.text,
      question_id: this.props.data.questionId,
    };

    request.postJSON(this.props.meta.saveUrl, answerData);
  }

  goToNextMode() {
    switch (this.state.mode) {
      case MODES.FEEDBACK_CORRECT:
        break;
      case MODES.FEEDBACK_INCORRECT:
        this.enterMode(MODES.READY);
        break;
      case MODES.READY: {
        const { currentChoice } = this.state;
        if (!currentChoice) {
          break;
        }
        if (currentChoice.correct) {
          this.enterMode(MODES.FEEDBACK_CORRECT);
        } else {
          this.enterMode(MODES.FEEDBACK_INCORRECT);
        }

        break;
      }
      default:
        break;
    }
  }

  buttonText() {
    return this.isInMode(MODES.FEEDBACK_INCORRECT) ||
      this.isInMode(MODES.FEEDBACK_CORRECT)
      ? I18n.t('questions.question.knowledge_check.try_again')
      : I18n.t('questions.question.knowledge_check.check_answer');
  }

  buttonIcon() {
    if (this.buttonText() === 'Try Again') {
      return 'refresh';
    }
    return '';
  }

  handleButtonClick() {
    if (!this.state.currentChoice) {
      return;
    }

    if (this.isInMode(MODES.READY)) {
      this.saveAnswer();
    }

    this.goToNextMode();
  }

  handleOptionsChange(e) {
    const { value } = e.target;
    this.setState(state => ({
      currentChoice: find(state.options, { value }),
    }));
  }

  selectedValue() {
    if (!this.state.currentChoice) {
      return null;
    }
    return this.state.currentChoice.value;
  }

  buttonDisabled() {
    return !this.state.currentChoice || this.isInMode(MODES.FEEDBACK_CORRECT);
  }

  render() {
    return (
      <div className="KnowledgeCheckShow">
        <div className="Label Label--brand CustomColor--backgroundColor ">
          {t('questions.question.knowledge_check.badge')}
        </div>
        <QuestionElementPrompt
          required={this.props.data.required}
          subject={this.props.data.subject}
          elementHeaderClass={this.props.meta.elementHeaderClass}
          questionSubjectClass={this.props.meta.questionSubjectClass}
          labelErrorClass={this.props.meta.labelErrorClass}
        />
        {this.isInFeedbackMode() && (
          <KnowledgeCheckFeedback
            correct={this.state.currentChoice.correct}
            feedback={this.state.currentChoice.feedback}
          />
        )}
        <RadioFieldGroup
          name={`question-${this.props.data.questionId}-choices`}
          disabled={this.props.data.disabled || this.isInFeedbackMode()}
          selectedValue={this.selectedValue()}
          onChange={e => {
            this.handleOptionsChange(e);
          }}
        >
          {this.state.options.map(option => (
            <RadioField key={option.value} value={option.value}>
              {option.text}
            </RadioField>
          ))}
        </RadioFieldGroup>
        <div className="KnowledgeCheckShow-buttonContainer">
          <Button
            type="button"
            className="KnowledgeCheckButton-button"
            onClick={() => this.handleButtonClick()}
            disabled={this.buttonDisabled()}
            icon={this.buttonIcon()}
            ariaLabel={this.buttonText()}
          >
            {this.buttonText()}
          </Button>
        </div>
      </div>
    );
  }
}

export default KnowledgeCheckShow;
